import { NEWS_URL } from '../config/newsConfig';
import type { NewsItem, NewsSource } from '../types/news';
import { fetchWithCache } from './cache';
import { formatJalaliDateTime } from './jalali';

// Articles without a parseable time sort after every dated article.
const toTimestamp = (time: string): number => {
  const timestamp = Date.parse(time);

  return Number.isNaN(timestamp) ? -Infinity : timestamp;
};

// Collapses the per-website groups into one list, newest first. Every article
// keeps its website as a `source` object and gains its Jalali publication time.
const toNewsItems = (sources: NewsSource[]): NewsItem[] =>
  sources
    .flatMap(source =>
      source.items.map(item => ({
        ...item,
        source: { title: source.title },
        jalaliTime: formatJalaliDateTime(item.time),
      })),
    )
    .sort((first, second) => {
      const firstTime = toTimestamp(first.time);
      const secondTime = toTimestamp(second.time);

      return firstTime === secondTime ? 0 : secondTime - firstTime;
    });

export const fetchNews = async (): Promise<NewsItem[]> => {
  const sources = await fetchWithCache<NewsSource[]>('news', async () => {
    const response = await fetch(NEWS_URL);

    if (!response.ok) {
      throw new Error(`Failed to fetch news: ${response.status}`);
    }

    return (await response.json()) as NewsSource[];
  });

  return toNewsItems(sources);
};
